/*
 * Charms Workshop booking widget.
 *
 * STEP 1: initialise the Sesami Storefront SDK.                          [done]
 * STEP 2: load real availability, render Date + Time pickers,           [done]
 *         populate the bundle select, keep a live CTA price.
 * STEP 3: add the chosen variant to the Shopify cart with the booking   [done]
 *         as line-item properties, then send the customer to checkout.
 *
 * Scope: SOLO end-to-end. Group / Party are stubbed for later.
 *
 * --- Real Sesami SDK v0.0.3 contract (read from cdn.sesami.co/sdk.js) ---
 *   new SesamiSDK({ shopId, productId, variantId, quantity, timezone, autoLoad })
 *   getToken()                       -> sets this.accesstoken (POST /storefront)
 *   getAvailabilities({ start, length, timezone, variantId, shopId,
 *                       productId, quantity })
 *       -> [{ date: 'YYYY-MM-DD', slots: [{ startTime: 'YYYY-MM-DD HH:mm', ... }] }]
 *       (requires a token first; does NOT fetch one itself)
 *
 * We do NOT use the SDK's reserve()/book(): book() needs a customer object
 * and bypasses the Shopify cart/checkout. Instead we follow the SDK's own
 * getSesamiFields() pattern — add the variant to the cart with line-item
 * properties Date / Time / Timezone — which Sesami's app embed reconciles
 * into a booking at checkout, keeping native Shopify payment.
 */
(function () {
  'use strict';

  /* --------------------------- helpers --------------------------- */

  // Shopify-style money formatting — handles every money_format placeholder
  // (amount, amount_no_decimals, *_with_comma_separator, *_with_apostrophe_separator).
  function formatMoney(cents, format) {
    var formatString = format || '€{{amount}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;

    function withDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      if (isNaN(number) || number == null) return '0';
      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var frac = parts[1] ? decimal + parts[1] : '';
      return whole + frac;
    }

    var match = formatString.match(placeholder);
    var value = '';
    switch (match ? match[1] : 'amount') {
      case 'amount': value = withDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ''); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
      default: value = withDelimiters(cents, 2);
    }
    return formatString.replace(placeholder, value);
  }

  function elem(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  var WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  // Calendar starts on Monday: getDay() indices in display order Mon..Sun.
  var WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];

  // Local 'YYYY-MM-DD' (NOT toISOString — that shifts by UTC offset).
  function ymd(d) {
    var m = d.getMonth() + 1, day = d.getDate();
    return d.getFullYear() + '-' + (m < 10 ? '0' + m : m) + '-' + (day < 10 ? '0' + day : day);
  }
  function todayIso() {
    var d = new Date(); d.setHours(0, 0, 0, 0);
    return ymd(d);
  }
  // n days from today as 'YYYY-MM-DD'.
  function isoPlusDays(n) {
    var d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() + n);
    return ymd(d);
  }
  // 'YYYY-MM-DD' -> { top: 'TODAY'|'TUE', bottom: '5' }
  function dateChipLabel(dateStr) {
    var d = new Date(dateStr + 'T00:00:00');
    return {
      top: dateStr === todayIso() ? 'TODAY' : WEEKDAYS[d.getDay()],
      bottom: String(d.getDate())
    };
  }

  /* ---- month helpers (navigable calendar) ---- */
  function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
  function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
  function addMonths(d, n) { return new Date(d.getFullYear(), d.getMonth() + n, 1); }
  function monthLabel(d) {
    try { return new Intl.DateTimeFormat(navigator.language || 'en-US', { month: 'long', year: 'numeric' }).format(d); }
    catch (e) { return d.getFullYear() + '-' + (d.getMonth() + 1); }
  }

  // A slot counts as bookable unless the API explicitly reports 0 capacity.
  function slotAvailable(slot) {
    if (slot && typeof slot.remainingSlots === 'number') return slot.remainingSlots > 0;
    if (slot && typeof slot.status === 'string') return slot.status.toLowerCase() !== 'unavailable';
    return true;
  }

  // Toggle a grid-rows collapse wrapper (see the .liquid markup). The wrapper
  // animates between grid-rows-[0fr] and grid-rows-[1fr] via Tailwind.
  function setCollapse(el, open) {
    if (!el) return;
    el.classList.toggle('grid-rows-[1fr]', open);
    el.classList.toggle('grid-rows-[0fr]', !open);
  }

  // Opacity fade helpers (elements carry `transition-opacity` in the markup).
  // `then` runs after the fade-out so content can swap while invisible.
  var FADE_MS = 200;
  function fadeOut(el, then) {
    if (!el) { if (then) then(); return; }
    el.classList.add('opacity-0');
    setTimeout(function () { if (then) then(); }, FADE_MS);
  }
  function fadeIn(el) {
    if (el) el.classList.remove('opacity-0');
  }

  // Cursor-following glow, delegated on a container so re-rendered chips keep
  // working. Sets --glow-x / --glow-y on whichever .wb-chip is under the cursor.
  function bindChipGlow(container) {
    if (!container || container.__wbGlow) return;
    container.__wbGlow = true;
    container.addEventListener('mousemove', function (e) {
      var chip = e.target.closest && e.target.closest('.wb-chip');
      if (!chip || !container.contains(chip)) return;
      var r = chip.getBoundingClientRect();
      chip.style.setProperty('--glow-x', (e.clientX - r.left) + 'px');
      chip.style.setProperty('--glow-y', (e.clientY - r.top) + 'px');
    });
  }

  // Variant titles look like "90 minutes / Bundle: Necklace & 5 charms".
  // We drop the duration segment, then split the rest into a category label
  // ("Bundle") and the bundle name ("Necklace & 5 charms").
  function parseVariantLabel(label) {
    var seg = String(label || '').split(' / ').pop().trim();
    var idx = seg.indexOf(':');
    if (idx === -1) return { category: '', name: seg };
    return { category: seg.slice(0, idx).trim(), name: seg.slice(idx + 1).trim() };
  }

  // Builds the 3-span layout (category, name, price) shared by the trigger and
  // each option row.
  function bundleSpans(target, parsed, priceStr) {
    target.innerHTML = '';
    var main = elem('span', 'wb-bundle-main flex items-baseline gap-1.5');
    if (parsed.category) main.appendChild(elem('span', 'wb-bundle-cat opacity-60', parsed.category));
    main.appendChild(elem('span', 'wb-bundle-name', parsed.name));
    target.appendChild(main);
    target.appendChild(elem('span', 'wb-bundle-price', priceStr));
  }

  // Bundle variants -> dropdown items (3-span render). Reused for Solo + guests.
  function bundleItems(variants, moneyFormat) {
    return (variants || []).map(function (v) {
      return {
        id: v.variantId,
        render: function (target) { bundleSpans(target, parseVariantLabel(v.label), formatMoney(v.price, moneyFormat)); }
      };
    });
  }

  // Plain text dropdown items (e.g. times). 'values' is an array of strings.
  function textItems(values) {
    return (values || []).map(function (val) {
      return { id: val, render: function (target) { target.textContent = val; } };
    });
  }

  // Reusable custom dropdown (the workshop bundle select). `items` is
  // [{ id, render: fn(target) }]; the only thing that varies between the bundle
  // and time dropdowns is what each item renders. Returns { el, getValue }.
  function createDropdown(ctx, items, selectedId, onSelect) {
    var open = false;
    var value = selectedId != null ? selectedId : (items[0] || {}).id;

    var container = elem('div', 'wb-bundle-dd relative');
    var trigger = elem('button', 'wb-bundle-trigger w-full flex items-center gap-2 bg-background/30 text-foreground text-sm md:text-sm px-2 py-2.5 rounded-xs');
    trigger.type = 'button';
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    var current = elem('span', 'flex-1 flex items-center justify-between gap-2 text-left');
    var arrow = elem('span', 'shrink-0 transition-transform duration-250 ease-medium');
    var arrowIcon = ctx.icon('arrow-down');
    if (arrowIcon) arrow.appendChild(arrowIcon);
    trigger.appendChild(current);
    trigger.appendChild(arrow);
    var list = elem('ul', 'wb-bundle-list absolute left-0 right-0 bottom-full mb-1 z-20 bg-background text-foreground text-sm md:text-sm rounded-xs max-h-60 overflow-auto hidden no-scrollbar');
    list.setAttribute('role', 'listbox');
    list.tabIndex = -1;
    container.appendChild(trigger);
    container.appendChild(list);

    function itemById(id) {
      return items.filter(function (it) { return String(it.id) === String(id); })[0] || items[0];
    }
    function syncCurrent() {
      var it = itemById(value);
      if (it) { current.innerHTML = ''; it.render(current); }
    }
    function setOpen(o) {
      open = o;
      list.classList.toggle('hidden', !o);
      trigger.setAttribute('aria-expanded', o ? 'true' : 'false');
      arrow.classList.toggle('rotate-180', o);
      if (o) { var s = list.querySelector('[aria-selected="true"]') || list.firstElementChild; if (s) s.focus(); }
    }
    function select(id) {
      value = id;
      Array.prototype.forEach.call(list.children, function (li) {
        li.setAttribute('aria-selected', li.getAttribute('data-id') === String(id) ? 'true' : 'false');
      });
      syncCurrent();
      setOpen(false);
      trigger.focus();
      if (onSelect) onSelect(id);
    }

    items.forEach(function (it) {
      var li = elem('li', 'wb-bundle-option flex items-center justify-between gap-2 px-2 py-2.5 cursor-pointer hover:bg-foreground/5 aria-selected:bg-foreground/10');
      li.setAttribute('role', 'option');
      li.setAttribute('data-id', it.id);
      li.setAttribute('aria-selected', String(value) === String(it.id) ? 'true' : 'false');
      li.tabIndex = -1;
      it.render(li);
      li.addEventListener('click', function () { select(it.id); });
      list.appendChild(li);
    });

    trigger.addEventListener('click', function () { setOpen(!open); });
    trigger.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(true); }
    });
    list.addEventListener('keydown', function (e) {
      var opts = Array.prototype.slice.call(list.children);
      var i = opts.indexOf(document.activeElement);
      if (e.key === 'ArrowDown') { e.preventDefault(); (opts[i + 1] || opts[0]).focus(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); (opts[i - 1] || opts[opts.length - 1]).focus(); }
      else if (e.key === 'Home') { e.preventDefault(); opts[0].focus(); }
      else if (e.key === 'End') { e.preventDefault(); opts[opts.length - 1].focus(); }
      else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (opts[i]) select(opts[i].getAttribute('data-id')); }
      else if (e.key === 'Escape') { e.preventDefault(); setOpen(false); trigger.focus(); }
    });
    document.addEventListener('mousedown', function (e) {
      if (open && !container.contains(e.target)) setOpen(false);
    });

    syncCurrent();
    return { el: container, getValue: function () { return value; } };
  }

  // A bundle picker = one dropdown + a link that adds/removes a single extra
  // dropdown (max two). Used by Solo and each Group guest. The second select
  // wipes open/closed with a height animation.
  //   ctx = { moneyFormat, icon, addLabel, removeLabel }
  // Returns { el, getValues } where getValues() -> [v1] or [v1, v2].
  function createGuestPicker(ctx, variants, onChange) {
    var picker = elem('div', 'wb-picker flex flex-col gap-0');
    var items = bundleItems(variants, ctx.moneyFormat);
    var defId = (items[0] || {}).id;
    var values = [];
    var collapse = null; // wipe wrapper for the second select
    var second = null;

    var primary = createDropdown(ctx, items, defId, function (id) {
      values[0] = id;
      if (onChange) onChange();
    });
    values[0] = primary.getValue();
    picker.appendChild(primary.el);

    var toggle = elem('button', 'wb-picker__toggle btn btn--tertiary border-b text-foreground border-foreground p pt-1 mt-1.5 pb-0.5 leading-none self-start', ctx.addLabel || 'Add a second workshop');
    toggle.type = 'button';

    function addSecond() {
      collapse = elem('div', 'grid grid-cols-1 grid-rows-[0fr] transition-[grid-template-rows] duration-300 ease-medium');
      var inner = elem('div', 'overflow-hidden');
      second = createDropdown(ctx, items, defId, function (id) {
        values[1] = id;
        if (onChange) onChange();
      });
      values[1] = second.getValue();
      inner.appendChild(second.el);
      //add a class to this new dropdown's child element wb-bundle-dd
      var dd = inner.querySelector('.wb-bundle-dd');
      if (dd) dd.classList.add('mt-1.5');
      // Optional note under the second select (Solo only — see buildSoloPicker ctx). Lives
      // inside the collapse wrapper, so it wipes open/closed with the second select and is
      // removed automatically when the second select is removed.
      if (ctx.secondNote) {
        var note = elem('div', 'wb-picker__note rte p opacity-70 mt-1.5');
        note.innerHTML = ctx.secondNote;
        inner.appendChild(note);
      }
      collapse.appendChild(inner);
      picker.insertBefore(collapse, toggle); // second select right under the first

      // Wipe open, then let the second dropdown's popover overflow.
      requestAnimationFrame(function () {
        collapse.classList.remove('grid-rows-[0fr]');
        collapse.classList.add('grid-rows-[1fr]');
      });
      var opened = function (e) {
        if (e && e.propertyName && e.propertyName !== 'grid-template-rows') return;
        inner.style.overflow = 'visible';
        collapse.removeEventListener('transitionend', opened);
      };
      collapse.addEventListener('transitionend', opened);
      setTimeout(opened, 400);

      toggle.textContent = ctx.removeLabel || 'Remove second workshop';
      if (onChange) onChange();
    }

    function removeSecond() {
      if (!collapse) return;
      var wrap = collapse, inner = wrap.firstElementChild;
      collapse = null; second = null;
      values.length = 1;

      if (inner) inner.style.overflow = ''; // clip again so the close animates cleanly
      wrap.classList.remove('grid-rows-[1fr]');
      wrap.classList.add('grid-rows-[0fr]');
      var closed = function (e) {
        if (e && e.propertyName && e.propertyName !== 'grid-template-rows') return;
        if (wrap.parentNode) wrap.parentNode.removeChild(wrap);
        wrap.removeEventListener('transitionend', closed);
      };
      wrap.addEventListener('transitionend', closed);
      setTimeout(closed, 400);

      toggle.textContent = ctx.addLabel || 'Add a second workshop';
      if (onChange) onChange();
    }

    toggle.addEventListener('click', function () {
      if (second) removeSecond(); else addSecond();
    });
    picker.appendChild(toggle);

    return { el: picker, getValues: function () { return values.slice(); } };
  }

  // Half-hour 'HH:MM' strings from start..end hours (e.g. 9, 22.5).
  function halfHourTimes(start, end) {
    var out = [];
    var s = Math.round((start || 0) * 2), e = Math.round((end || 24) * 2);
    for (var i = s; i <= e; i++) {
      var hh = Math.floor(i / 2);
      out.push(('0' + hh).slice(-2) + ':' + ((i % 2) ? '30' : '00'));
    }
    return out;
  }

  // 'YYYY-MM-DD' -> 'TUE 23.06.26'
  function partyDateLabel(iso) {
    var d = new Date(iso + 'T00:00:00');
    var wd = WEEKDAYS[d.getDay()];
    var dd = ('0' + d.getDate()).slice(-2);
    var mm = ('0' + (d.getMonth() + 1)).slice(-2);
    return wd + ' ' + dd + '.' + mm + '.' + String(d.getFullYear()).slice(-2);
  }

  // +/- stepper. ctx = { icon, min, max, value, fieldClass }.
  // Returns { el, getValue }.
  function createStepper(ctx, onChange) {
    var min = ctx.min != null ? ctx.min : 1;
    var max = ctx.max != null ? ctx.max : 16;
    var value = Math.max(min, Math.min(max, ctx.value != null ? ctx.value : min));

    var wrap = elem('div', 'wb-stepper relative overflow-hidden flex items-center gap-2 ' + (ctx.fieldClass || 'bg-background/30') + ' text-foreground rounded-xs self-start');
    var minus = elem('button', 'wb-stepper__btn flex items-center justify-center px-3 py-2.5 disabled:opacity-30');
    minus.type = 'button'; minus.setAttribute('aria-label', 'Fewer');
    var mi = ctx.icon('minus'); if (mi) minus.appendChild(mi);
    var val = elem('span', 'wb-stepper__value min-w-6 text-center text-sm font-semibold', String(value));
    var plus = elem('button', 'wb-stepper__btn flex items-center justify-center px-3 py-2.5 disabled:opacity-30');
    plus.type = 'button'; plus.setAttribute('aria-label', 'More');
    var pi = ctx.icon('plus'); if (pi) plus.appendChild(pi);
    wrap.appendChild(minus); wrap.appendChild(val); wrap.appendChild(plus);

    function render() {
      val.textContent = String(value);
      minus.disabled = value <= min;
      plus.disabled = value >= max;
    }
    function set(n) { value = Math.max(min, Math.min(max, n)); render(); if (onChange) onChange(value); }
    minus.addEventListener('click', function () { set(value - 1); });
    plus.addEventListener('click', function () { set(value + 1); });
    wrap.addEventListener('mousemove', function (e) {
      var r = wrap.getBoundingClientRect();
      wrap.style.setProperty('--glow-x', (e.clientX - r.left) + 'px');
      wrap.style.setProperty('--glow-y', (e.clientY - r.top) + 'px');
    });

    render();
    if (onChange) onChange(value); // seed the bound value
    return { el: wrap, getValue: function () { return value; } };
  }

  // Date picker: a field that wipes open a month calendar, select, wipes closed
  // with the chosen date in the field. ctx = { icon, monthsAhead, placeholder, fieldClass }.
  // Returns { field, calendar, getValue } so the caller can place the field and
  // the (full-width) calendar in separate spots. getValue -> 'YYYY-MM-DD' | null.
  function createDatePicker(ctx, onSelect) {
    var open = false, selected = null;
    var view = startOfMonth(new Date());

    var field = elem('button', 'wb-datepicker__field w-full ' + (ctx.fieldClass || 'bg-background') + ' text-foreground p text-start  px-2 py-2.75 rounded-xs', ctx.placeholder || 'Select a date');
    field.type = 'button';
    var collapse = elem('div', 'wb-datepicker__cal grid grid-cols-1 grid-rows-[0fr] transition-[grid-template-rows] duration-300 ease-medium');
    var inner = elem('div', 'overflow-hidden');
    var cal = elem('div', 'wb-calendar flex flex-col gap-2 pt-2');
    inner.appendChild(cal); collapse.appendChild(inner);

    function navBtn(name, disabled, onClick) {
      var b = elem('button', 'wb-cal-nav'); b.type = 'button'; b.disabled = disabled;
      var ic = ctx.icon(name); if (ic) b.appendChild(ic);
      b.addEventListener('click', onClick);
      return b;
    }
    function dayCell(iso) {
      var isPast = iso < todayIso();
      var chip = elem('button', 'wb-chip wb-date group relative flex flex-col rounded-sm overflow-hidden border-background/30 border-1 is-active:border-background disabled:opacity-30');
      chip.type = 'button'; chip.disabled = isPast;
      chip.appendChild(elem('span', 'wb-chip__bottom bg-background/30 text-foreground p px-2 py-1.5 group-[.is-active]:bg-background', String(new Date(iso + 'T00:00:00').getDate())));
      if (iso === selected) chip.classList.add('is-active');
      if (!isPast) chip.addEventListener('click', function () { pick(iso); });
      return chip;
    }
    function renderCal() {
      cal.innerHTML = '';
      var minMonth = startOfMonth(new Date());
      var maxMonth = addMonths(minMonth, ctx.monthsAhead || 12);
      var header = elem('div', 'wb-calendar__header flex items-center justify-between gap-2');
      header.appendChild(navBtn('arrow-left', view.getTime() <= minMonth.getTime(), function () { view = addMonths(view, -1); renderCal(); }));
      header.appendChild(elem('span', 'wb-calendar__label p font-semibold', monthLabel(view)));
      header.appendChild(navBtn('arrow-right', view.getTime() >= maxMonth.getTime(), function () { view = addMonths(view, 1); renderCal(); }));
      cal.appendChild(header);
      var grid = elem('div', 'wb-calendar__grid grid grid-cols-7 gap-1');
      for (var w = 0; w < 7; w++) grid.appendChild(elem('span', 'wb-weekday p text-foreground text-center', WEEKDAYS[WEEK_ORDER[w]]));
      var lead = (startOfMonth(view).getDay() + 6) % 7;
      for (var b = 0; b < lead; b++) grid.appendChild(elem('span', 'wb-cal-blank'));
      var dim = endOfMonth(view).getDate();
      for (var d = 1; d <= dim; d++) grid.appendChild(dayCell(ymd(new Date(view.getFullYear(), view.getMonth(), d))));
      cal.appendChild(grid);
    }
    function setOpen(o) { open = o; setCollapse(collapse, o); if (o) renderCal(); }
    function pick(iso) {
      selected = iso;
      field.textContent = partyDateLabel(iso);
      setOpen(false);
      if (onSelect) onSelect(iso, partyDateLabel(iso));
    }
    field.addEventListener('click', function () { setOpen(!open); });
    document.addEventListener('mousedown', function (e) {
      if (open && !field.contains(e.target) && !collapse.contains(e.target)) setOpen(false);
    });

    bindChipGlow(cal);
    return { field: field, calendar: collapse, getValue: function () { return selected; } };
  }

  function whenSdkReady(timeoutMs) {
    return new Promise(function (resolve) {
      if (typeof window.SesamiSDK === 'function') return resolve(true);
      var settled = false;
      function finish() { if (!settled) { settled = true; resolve(typeof window.SesamiSDK === 'function'); } }
      var prev = window.sesamiAsyncInit;
      window.sesamiAsyncInit = function () {
        if (typeof prev === 'function') { try { prev(); } catch (e) {} }
        finish();
      };
      setTimeout(finish, timeoutMs || 10000);
    });
  }

  var DATE_PREVIEW_COUNT = 5; // chips shown before the "MORE" cell

  /* --------------------------- controller --------------------------- */

  function Booking(root) {
    this.root = root;
    var configEl = root.querySelector('[data-workshop-booking-config]');
    this.config = configEl ? JSON.parse(configEl.textContent) : {};

    var types = this.config.types || {};
    this.types = types;
    // The active type is fixed per page (the type tabs are now links to each
    // type's own page). Fall back to whichever type has a product configured.
    this.typeKey = this.config.activeType
      || ((types.solo && types.solo.productId) ? 'solo'
        : (types.group && types.group.productId) ? 'group' : 'party');
    this.type = types[this.typeKey] || null;

    this.sesami = null;
    this.byDate = {};                 // 'YYYY-MM-DD' -> [ slot, ... ]
    this.datesExpanded = false;
    this.selectedDate = null;
    this.selectedSlot = null;
    this.variantId = (this.type && this.type.variants && this.type.variants[0] || {}).variantId;

    // Pickers (one bundle dropdown + optional second). Solo has one; Group has
    // one per guest.
    this.soloPicker = null;
    this.guestPickers = [];

    // Group flow state.
    this.groupStep = 1;                  // 1 = date/time/participants, 2 = guests
    this.participants = this.config.groupMin || 2;

    this.elBody = root.querySelector('[data-wb-body]');
    this.elLoading = root.querySelector('[data-wb-loading]');
    this.elForm = root.querySelector('[data-wb-form]');
    this.elTimeCollapse = root.querySelector('[data-wb-time-collapse]');
    this.elBundleSolo = root.querySelector('[data-wb-bundle]');
    this.elBundlePicker = root.querySelector('[data-wb-bundle-picker]');
    this.elParty = root.querySelector('[data-wb-party]');
    this.elTypeRow = root.querySelector('[data-wb-type-row]');
    this.elCtaRow = root.querySelector('[data-wb-cta-row]');
    this.elDates = root.querySelector('[data-wb-dates]');
    this.elTimes = root.querySelector('[data-wb-times]');
    // Group hooks.
    this.elStep1 = root.querySelector('[data-wb-step1]');
    this.elSteps = root.querySelector('[data-wb-steps]');
    this.elStep2 = root.querySelector('[data-wb-step2]');
    this.elParticipants = root.querySelector('[data-wb-participants]');
    this.elStepper = root.querySelector('[data-wb-stepper]');
    this.elPtMinus = root.querySelector('[data-wb-pt-minus]');
    this.elPtPlus = root.querySelector('[data-wb-pt-plus]');
    this.elPtValue = root.querySelector('[data-wb-pt-value]');
    this.elGuests = root.querySelector('[data-wb-guests]');
    this.elBack = root.querySelector('[data-wb-back]');
    this.elCta = root.querySelector('[data-wb-cta]');
    this.elCtaLabel = root.querySelector('[data-wb-cta-label]');
    this.elCtaPrice = root.querySelector('[data-wb-cta-price]');
    this.elIncluded = root.querySelector('[data-wb-included]');
    this.elError = root.querySelector('[data-wb-error]');

    this._start();
  }

  Booking.prototype._start = function () {
    var self = this;

    // The card only becomes a scroll container when its content genuinely
    // overflows the max-height (e.g. group step 2 with many guests). Otherwise
    // it stays non-scrollable, so it can't be rubber-band/inner-scrolled while
    // the content comfortably fits.
    this._watchCardScroll();

    this.applyActiveType();
    this.bindCta();
    bindChipGlow(this.elDates);
    bindChipGlow(this.elTimes);

    // The current type's tab links to this same page — don't let it reload.
    var activeTab = this.root.querySelector('[data-wb-type].is-active');
    if (activeTab) activeTab.addEventListener('click', function (e) { e.preventDefault(); });

    // Party uses the native Shopify contact form — no Sesami/availability flow,
    // but we build the JS date picker + guests stepper for it.
    if (this.typeKey === 'party') { this.setupParty(); return; }

    if (!this.type || !this.type.productId) {
      console.error('[workshop-booking] No product configured for type:', this.typeKey);
      return;
    }

    if (this.typeKey === 'group') this.setupGroup();
    else this.buildSoloPicker();
    this.refreshCta();

    whenSdkReady().then(function (ready) {
      if (!ready) {
        self.showError('Booking is temporarily unavailable. Please try again shortly.');
        console.error('[workshop-booking] Sesami SDK not available.');
        return;
      }

      self.sesami = window.sesami = new SesamiSDK({
        shopId: self.config.shopId,
        productId: self.type.productId,
        variantId: self.variantId,
        quantity: 1,
        timezone: self.config.timezone || null
      });

      self.loadAvailability();
    });
  };

  // Ensure a token (app embed may have set window.SesamiShopify.accessToken;
  // otherwise the SDK fetches one via getToken()).
  Booking.prototype.ensureToken = function () {
    if (this.sesami.accesstoken) return Promise.resolve(true);
    return Promise.resolve(this.sesami.getToken()).catch(function () { return false; });
  };

  // The Sesami /availabilities endpoint returns ~a week per call, so we fetch
  // in canonical 7-day chunks (offsets from today) and cache by chunk index.
  Booking.prototype.fetchRange = function (startStr, len) {
    var self = this;
    return this.ensureToken().then(function () {
      return self.sesami.getAvailabilities({
        start: startStr,
        length: len,
        timezone: self.config.timezone || null,
        variantId: self.variantId,
        shopId: self.config.shopId,
        productId: self.type.productId,
        quantity: 1
      });
    }).then(function (result) {
      self.indexAvailability(Array.isArray(result) ? result : []);
    }).catch(function (err) {
      console.error('[workshop-booking] availability fetch failed:', err);
    });
  };

  Booking.prototype.fetchWeek = function (k) {
    this.fetchedWeeks = this.fetchedWeeks || {};
    if (this.fetchedWeeks[k]) return Promise.resolve();
    this.fetchedWeeks[k] = true;
    return this.fetchRange(isoPlusDays(k * 7), 7);
  };

  // Ensure every canonical week overlapping the given month is loaded.
  // Resolves with `true` if anything new was actually fetched.
  Booking.prototype.ensureMonthLoaded = function (monthDate) {
    var DAY = 86400000;
    var today0 = new Date(todayIso() + 'T00:00:00');
    var first = startOfMonth(monthDate);
    var last = endOfMonth(monthDate);
    var rangeStart = first.getTime() < today0.getTime() ? today0 : first;
    var kStart = Math.max(0, Math.floor((rangeStart.getTime() - today0.getTime()) / (7 * DAY)));
    var kEnd = Math.floor((last.getTime() - today0.getTime()) / (7 * DAY));

    this.fetchedWeeks = this.fetchedWeeks || {};
    var promises = [];
    for (var k = kStart; k <= kEnd; k++) {
      if (!this.fetchedWeeks[k]) promises.push(this.fetchWeek(k));
    }
    return Promise.all(promises).then(function () { return promises.length > 0; });
  };

  Booking.prototype.loadAvailability = function () {
    var self = this;
    // Two weeks is enough for the collapsed strip; months load on demand.
    Promise.all([this.fetchWeek(0), this.fetchWeek(1)]).then(function () {
      self.renderDates();
      self.revealForm();
    });
  };

  // A collapse wrapper is overflow-hidden so its open/close animates cleanly.
  // Once open, let it overflow so absolute dropdowns can escape the clip.
  Booking.prototype._unclip = function (collapseEl) {
    var inner = collapseEl && collapseEl.firstElementChild;
    if (!inner) return;
    var done = function (e) {
      if (e && e.propertyName && e.propertyName !== 'grid-template-rows') return;
      inner.style.overflow = 'visible';
      collapseEl.removeEventListener('transitionend', done);
    };
    collapseEl.addEventListener('transitionend', done);
    setTimeout(done, 700); // fallback if transitionend doesn't fire
  };

  // Swap the loading state for the form (animated).
  Booking.prototype.revealForm = function () {
    setCollapse(this.elLoading, false);
    setCollapse(this.elForm, true);
    // Once open, let the form overflow so the bundle/guest dropdowns can overlay.
    this._unclip(this.elForm);
  };

  // API shape: [{ date, slots: [{ startTime: 'YYYY-MM-DD HH:mm', ... }] }]
  // Merges into the running cache so multiple month/week fetches accumulate.
  Booking.prototype.indexAvailability = function (list) {
    this.byDate = this.byDate || {};
    var self = this;
    list.forEach(function (day) {
      if (!day || !day.date) return;
      self.byDate[day.date] = (day.slots || []).map(function (s) {
        return {
          time: String(s.startTime || '').split(' ')[1] || '',
          startTime: s.startTime,
          remainingSlots: s.remainingSlots,
          status: s.status,
          raw: s
        };
      });
    });
  };

  /* ----------------------------- bundle ----------------------------- */

  // Custom listbox (a native <select> can't hold the 3-span option layout).
  Booking.prototype.buildSoloPicker = function () {
    var self = this;
    if (!this.elBundlePicker) return;
    this.elBundlePicker.innerHTML = '';
    var ctx = {
      moneyFormat: this.config.moneyFormat,
      icon: function (n) { return self.icon(n); },
      addLabel: this.config.addSecondLabel || 'Add a second workshop',
      removeLabel: this.config.removeSecondLabel || 'Remove second workshop',
      // Solo only: nudge multi-person bookings toward the group workshop (group guests
      // get no note — they're already booking for multiple people).
      secondNote: this.config.soloSecondNote || ''
    };
    this.soloPicker = createGuestPicker(ctx, (this.type.variants || []), function () { self.refreshCta(); });
    this.elBundlePicker.appendChild(this.soloPicker.el);
  };

  // Sum the prices of a list of variant ids.
  Booking.prototype._sumPrices = function (ids) {
    var variants = (this.type && this.type.variants) || [];
    var total = 0;
    (ids || []).forEach(function (id) {
      var v = variants.filter(function (x) { return String(x.variantId) === String(id); })[0];
      if (v) total += v.price;
    });
    return total;
  };

  Booking.prototype._price = function () {
    return this.soloPicker ? this._sumPrices(this.soloPicker.getValues()) : 0;
  };

  /* ------------------------------ dates ----------------------------- */

  // Collapsed strip: 4 days + MORE on mobile, 5 days + MORE on desktop.
  // Expanded: a weekday-aligned calendar grid (7 cols).
  Booking.prototype.setDatesLayout = function (mode) {
    if (!this.elDates) return;
    var cl = this.elDates.classList;
    cl.remove('grid-cols-1', 'grid-cols-5', 'md:grid-cols-6', 'grid-cols-6', 'grid-cols-7');
    // Calendar + bar are single full-width columns; the calendar's own inner
    // grid (.wb-calendar__grid) handles the 7 weekday columns.
    if (mode === 'calendar' || mode === 'bar') cl.add('grid-cols-1');
    else if (mode === 'strip') cl.add('grid-cols-5', 'md:grid-cols-6');
    else cl.add('grid-cols-6');
  };

  // Clone a theme icon (rendered into a <template> in the .liquid) for use in
  // JS-built UI like the calendar nav.
  Booking.prototype.icon = function (name) {
    var tpl = this.root.querySelector('[data-wb-icon="' + name + '"]');
    if (!tpl) return null;
    if (tpl.content && tpl.content.firstElementChild) return tpl.content.firstElementChild.cloneNode(true);
    var tmp = document.createElement('span');
    tmp.innerHTML = tpl.innerHTML;
    return tmp.firstElementChild;
  };

  Booking.prototype.renderDates = function () {
    if (!this.elDates) return;
    this.elDates.innerHTML = '';
    // Collapsed: day strip. Expanded: month calendar, or — once a date is
    // picked — a single bar showing it (click to reopen the calendar).
    if (!this.datesExpanded) { this.renderDateStrip(this.config.daysAhead || 14); return; }
    if (this.calendarOpen) this.renderMonth();
    else this.renderSelectedBar();
  };

  // Collapsed calendar: a single bar with the chosen date, styled like the
  // type tabs. Clicking it reopens the month grid.
  Booking.prototype.renderSelectedBar = function () {
    var self = this;
    this.setDatesLayout('bar');
    var label;
    try {
      label = new Intl.DateTimeFormat(navigator.language || 'en-US', { weekday: 'long', day: 'numeric', month: 'long' })
        .format(new Date(this.selectedDate + 'T00:00:00'));
    } catch (e) { label = this.selectedDate; }

    var bar = elem('button', 'wb-date-bar w-full flex items-center justify-start gap-2 bg-background hover:bg-background/50 text-foreground p px-3 pt-2.5 pb-2.25 leading-none rounded-xs transition-colors duration-250', label);
    bar.type = 'button';
    bar.setAttribute('aria-label', 'Change date');
    bar.addEventListener('click', function () { self.calendarOpen = true; self.renderDates(); });
    this.elDates.appendChild(bar);
  };

  Booking.prototype.renderDateStrip = function (total) {
    var self = this;
    this.setDatesLayout('strip');

    var shown = Math.min(DATE_PREVIEW_COUNT, total);
    for (var i = 0; i < shown; i++) {
      var chip = this.dateChip(isoPlusDays(i));
      // 5th day shows on desktop only — mobile defaults to 4 days + MORE.
      if (i === DATE_PREVIEW_COUNT - 1) chip.classList.add('max-md:hidden');
      this.elDates.appendChild(chip);
    }
    if (total > DATE_PREVIEW_COUNT) {
      var more = elem('button', 'wb-chip wb-date group relative flex flex-col rounded-sm overflow-hidden border-background/30 border-1 is-active:border-background');

      more.type = 'button';
      var moreTop = elem('span', 'wb-chip__top-wrapper h4 text-foreground overflow-hidden');
      moreTop.appendChild(elem('span', 'wb-chip__top w-full h-full inline-block px-2 py-1.5 rounded-xs shadow-[0px_0px_0px_8px_white] shadow-background/30 group-[.is-active]:shadow-background', 'MORE'));
      more.appendChild(moreTop);
      more.appendChild(elem('span', 'wb-chip__bottom bg-background/30 text-foreground p px-2 py-1.5 group-[.is-active]:bg-background', '…'));
      more.addEventListener('click', function () {
        self.datesExpanded = true;
        self.calendarOpen = true;
        self.viewMonth = startOfMonth(new Date());
        self.renderDates();
      });
      this.elDates.appendChild(more);
    }
  };

  // Navigable month calendar. Loads the viewed month's availability on demand
  // (up to monthsAhead), so people can book several months in advance.
  Booking.prototype.renderMonth = function () {
    var self = this;
    this.elDates.innerHTML = '';
    this.setDatesLayout('calendar');
    if (!this.viewMonth) this.viewMonth = startOfMonth(new Date());

    var view = this.viewMonth;
    var minMonth = startOfMonth(new Date());
    // Hard cap: never let people navigate more than 4 months ahead.
    var maxMonth = addMonths(minMonth, Math.min(this.config.monthsAhead || 4, 4));

    // Wrapper so the whole calendar can be styled as one block.
    var wrap = elem('div', 'wb-calendar bg-background/20 p-3 flex flex-col gap-4 rounded-sm overflow-hidden');

    // Header (prev / label / next).
    var header = elem('div', 'wb-calendar__header flex items-center justify-between gap-2');
    var prev = elem('button', 'wb-cal-nav');
    prev.type = 'button';
    prev.setAttribute('aria-label', 'Previous month');
    prev.disabled = view.getTime() <= minMonth.getTime();
    var prevIcon = this.icon('arrow-left');
    if (prevIcon) prev.appendChild(prevIcon);
    prev.addEventListener('click', function () { self.viewMonth = addMonths(self.viewMonth, -1); self.renderMonth(); });
    var next = elem('button', 'wb-cal-nav');
    next.type = 'button';
    next.setAttribute('aria-label', 'Next month');
    next.disabled = view.getTime() >= maxMonth.getTime();
    var nextIcon = this.icon('arrow-right');
    if (nextIcon) next.appendChild(nextIcon);
    next.addEventListener('click', function () { self.viewMonth = addMonths(self.viewMonth, 1); self.renderMonth(); });
    header.appendChild(prev);
    header.appendChild(elem('span', 'wb-calendar__label p font-semibold', monthLabel(view)));
    header.appendChild(next);
    wrap.appendChild(header);

    // Day grid (weekday header + leading blanks + day cells).
    var grid = elem('div', 'wb-calendar__grid grid grid-cols-7 gap-1');
    for (var w = 0; w < 7; w++) {
      grid.appendChild(elem('span', 'wb-weekday p text-foreground text-center', WEEKDAYS[WEEK_ORDER[w]]));
    }
    // Leading blanks before day 1, with Monday as the first column.
    var lead = (startOfMonth(view).getDay() + 6) % 7;
    for (var b = 0; b < lead; b++) {
      grid.appendChild(elem('span', 'wb-cal-blank'));
    }
    var dim = endOfMonth(view).getDate();
    for (var d = 1; d <= dim; d++) {
      grid.appendChild(this.dateChip(ymd(new Date(view.getFullYear(), view.getMonth(), d)), true));
    }
    wrap.appendChild(grid);

    this.elDates.appendChild(wrap);

    // Load this month's availability, then re-render once (only if new data
    // arrived, which avoids an infinite render loop). Skip if the user has
    // since collapsed the calendar or navigated away.
    this.ensureMonthLoaded(view).then(function (didFetch) {
      if (didFetch && self.datesExpanded && self.calendarOpen && self.viewMonth.getTime() === view.getTime()) {
        self.renderMonth();
      }
    });
  };

  Booking.prototype.dateChip = function (date, numberOnly) {
    var self = this;
    var lbl = dateChipLabel(date);
    var available = date >= todayIso() && (this.byDate[date] || []).some(slotAvailable);

    var chip = elem('button', 'wb-chip wb-date group relative flex flex-col rounded-sm overflow-hidden border-background/30 border-1 is-active:border-background disabled:opacity-30');
    chip.type = 'button';
    chip.setAttribute('data-date', date);
    chip.disabled = !available;
    var chipTop = elem('span', 'wb-chip__top-wrapper h4 text-foreground overflow-hidden');
    if (!numberOnly) chipTop.appendChild(elem('span', 'wb-chip__top w-full h-full inline-block px-2 py-1.5 rounded-xs shadow-[0px_0px_0px_8px_white] shadow-background/30 group-[.is-active]:shadow-background', lbl.top));
    if (!numberOnly) chip.appendChild(chipTop);
    chip.appendChild(elem('span', 'wb-chip__bottom bg-background/30 text-foreground p px-2 py-1.5 group-[.is-active]:bg-background', lbl.bottom));
    if (date === this.selectedDate) chip.classList.add('is-active');
    if (available) {
      chip.addEventListener('click', function () { self.selectDate(date); });
    }
    return chip;
  };

  Booking.prototype.selectDate = function (date) {
    this.selectedDate = date;
    this.selectedSlot = null;
    this.clearError();

    if (this.datesExpanded) {
      // Calendar mode: collapse the month grid back to the single date bar.
      this.calendarOpen = false;
      this.renderDates();
    } else {
      // Strip mode: just highlight the chosen day.
      Array.prototype.forEach.call(this.elDates.children, function (chip) {
        if (chip.getAttribute) chip.classList.toggle('is-active', chip.getAttribute('data-date') === date);
      });
    }

    this.renderTimes(this.byDate[date] || []);
    setCollapse(this.elTimeCollapse, true);
    this.refreshCta();
  };

  /* ------------------------------ times ----------------------------- */

  Booking.prototype.renderTimes = function (slots) {
    var self = this;
    if (!this.elTimes) return;
    this.elTimes.innerHTML = '';
    if (!slots.length) {
      this.elTimes.appendChild(elem('p', 'meta col-span-3', 'No times for this date.'));
      return;
    }
    slots.forEach(function (slot) {
      var chip = elem('button', 'wb-chip wb-time relative bg-background/30 h4 px-2 py-1.5 rounded-xs overflow-hidden is-active:bg-background');
      chip.appendChild(elem('span', null, slot.time));
      chip.type = 'button';
      chip.disabled = !slotAvailable(slot);
      if (!chip.disabled) {
        chip.addEventListener('click', function () { self.selectSlot(slot, chip); });
      }
      self.elTimes.appendChild(chip);
    });
  };

  Booking.prototype.selectSlot = function (slot, chip) {
    this.selectedSlot = slot;
    Array.prototype.forEach.call(this.elTimes.children, function (c) {
      if (c.classList) c.classList.toggle('is-active', c === chip);
    });
    this.refreshCta();
  };

  /* ------------------------------- CTA ------------------------------ */

  // Drives the primary CTA label / price / enabled state across all states:
  // Solo, Group step 1 (Continue), Group step 2 (Book your spots).
  Booking.prototype.refreshCta = function () {
    if (!this.elCta) return;
    var label, price = '', complete, showBack = false, showIncluded = true;

    if (this.typeKey === 'group') {
      if (this.groupStep === 2) {
        label = this.config.ctaLabelGroup || 'Book your spots';
        price = ' · ' + formatMoney(this._groupTotal(), this.config.moneyFormat);
        complete = true;
        showBack = true;
        showIncluded = false;
      } else {
        label = this.config.continueLabel || 'Continue to bundles';
        complete = !!this.selectedSlot; // date + time chosen
      }
    } else {
      label = this.config.ctaLabel || 'Book your spot';
      complete = !!this.selectedSlot;
      if (complete) price = ' · ‎ ' + formatMoney(this._price(), this.config.moneyFormat);
    }

    if (this.elCtaLabel) this.elCtaLabel.textContent = label;
    if (this.elCtaPrice) this.elCtaPrice.textContent = price;
    if (this.elCtaPrice) this.elCtaPrice.classList.toggle('hidden', !price);
    this.elCta.classList.toggle('disable', !complete);
    if (this.elBack) this.elBack.hidden = !showBack;
    if (this.elIncluded) this.elIncluded.hidden = !showIncluded;
  };

  Booking.prototype.bindCta = function () {
    var self = this;
    if (this.elCta && !this.elCta.__wbBound) {
      this.elCta.__wbBound = true;
      this.elCta.addEventListener('click', function (e) {
        e.preventDefault();
        if (self.elCta.classList.contains('disable') || self.elCta.classList.contains('is-loading')) return;
        if (self.typeKey === 'group') {
          if (self.groupStep === 2) self.bookGroup();
          else self.groupContinue();
        } else {
          self.book();
        }
      });
    }
    if (this.elBack && !this.elBack.__wbBound) {
      this.elBack.__wbBound = true;
      this.elBack.addEventListener('click', function (e) { e.preventDefault(); self.groupBack(); });
    }
  };

  // Line-item properties Sesami reads to build the booking.
  Booking.prototype._properties = function () {
    var parts = String(this.selectedSlot.startTime).split(' ');
    var p = { 'Date': parts[0], 'Time': parts[1] || '' };
    if (this.config.timezone) p['Timezone'] = this.config.timezone;
    return p;
  };

  // Clear any stale booking, add the given line items, then go to checkout.
  Booking.prototype._checkout = function (items) {
    var self = this;
    this.clearError();
    this.elCta.classList.add('is-loading');

    var cartUrl = this.config.cartUrl || '/cart';
    var headers = { 'Content-Type': 'application/json', 'Accept': 'application/json' };

    fetch(cartUrl + '/clear.js', { method: 'POST', headers: headers }).then(function () {
      return fetch(cartUrl + '/add.js', {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ items: items })
      });
    }).then(function (res) {
      if (!res.ok) return res.json().then(function (j) { throw j; });
      return res.json();
    }).then(function () {
      window.location.href = self.config.checkoutUrl || '/checkout';
    }).catch(function (err) {
      console.error('[workshop-booking] add to cart failed:', err);
      self.elCta.classList.remove('is-loading');
      self.showError((err && err.description) || 'Could not reserve your spot. Please try again.');
    });
  };

  // Solo: one seat per chosen workshop (1 or 2 line items).
  Booking.prototype.book = function () {
    if (!this.selectedSlot || !this.soloPicker) return;
    var props = this._properties();
    var items = this.soloPicker.getValues().map(function (id) {
      return { id: Number(id), quantity: 1, properties: props };
    });
    if (items.length) this._checkout(items);
  };

  /* ------------------------------ group ----------------------------- */

  Booking.prototype.setupGroup = function () {
    var self = this;
    var min = this.config.groupMin || 2;
    var max = this.config.maxParticipants || 8;
    this.participants = Math.max(min, Math.min(max, this.participants || min));

    // Stepper icons + bindings.
    if (this.elPtMinus && !this.elPtMinus.__wbIcon) { this.elPtMinus.__wbIcon = true; var mi = this.icon('minus'); if (mi) this.elPtMinus.appendChild(mi); }
    if (this.elPtPlus && !this.elPtPlus.__wbIcon) { this.elPtPlus.__wbIcon = true; var pi = this.icon('plus'); if (pi) this.elPtPlus.appendChild(pi); }
    if (this.elPtMinus && !this.elPtMinus.__wbBound) { this.elPtMinus.__wbBound = true; this.elPtMinus.addEventListener('click', function () { self.setParticipants(self.participants - 1); }); }
    if (this.elPtPlus && !this.elPtPlus.__wbBound) { this.elPtPlus.__wbBound = true; this.elPtPlus.addEventListener('click', function () { self.setParticipants(self.participants + 1); }); }

    // Cursor-following glow: feed the pointer position to the CSS gradient.
    if (this.elStepper && !this.elStepper.__wbGlow) {
      this.elStepper.__wbGlow = true;
      this.elStepper.addEventListener('mousemove', function (e) {
        var r = self.elStepper.getBoundingClientRect();
        self.elStepper.style.setProperty('--glow-x', (e.clientX - r.left) + 'px');
        self.elStepper.style.setProperty('--glow-y', (e.clientY - r.top) + 'px');
      });
    }

    this.renderStepper();
  };

  Booking.prototype.setParticipants = function (n) {
    var min = this.config.groupMin || 2;
    var max = this.config.maxParticipants || 8;
    this.participants = Math.max(min, Math.min(max, n));
    this.renderStepper();
  };

  Booking.prototype.renderStepper = function () {
    var min = this.config.groupMin || 2;
    var max = this.config.maxParticipants || 8;
    if (this.elPtValue) this.elPtValue.textContent = String(this.participants);
    if (this.elPtMinus) this.elPtMinus.disabled = this.participants <= min;
    if (this.elPtPlus) this.elPtPlus.disabled = this.participants >= max;
  };

  // Step 1 -> Step 2: build a bundle dropdown per guest, animate the swap.
  // Swap two steps: fade the current one out, then smoothly animate the
  // wrapper's height to the next step while fading it in (no jarring jump).
  Booking.prototype._fadeSwap = function (outEl, inEl) {
    var wrap = this.elSteps;

    // Lock the current height and clip while we swap.
    if (wrap) {
      wrap.style.height = wrap.offsetHeight + 'px';
      wrap.style.overflow = 'hidden';
    }

    fadeOut(outEl, function () {
      if (outEl) outEl.hidden = true;
      if (inEl) inEl.hidden = false;

      if (wrap && inEl) {
        var target = inEl.offsetHeight; // natural height of the incoming step
        requestAnimationFrame(function () {
          wrap.style.height = target + 'px';
          fadeIn(inEl);
        });
        var done = function (e) {
          if (e && (e.target !== wrap || (e.propertyName && e.propertyName !== 'height'))) return;
          wrap.style.height = '';     // release to auto (time picker etc. can grow it)
          wrap.style.overflow = '';   // release clip so guest dropdowns can overlay
          wrap.removeEventListener('transitionend', done);
        };
        wrap.addEventListener('transitionend', done);
        setTimeout(done, 600); // fallback
      } else if (inEl) {
        requestAnimationFrame(function () { fadeIn(inEl); });
      }
    });
  };

  Booking.prototype.groupContinue = function () {
    var self = this;
    if (!this.selectedSlot) return;
    this.buildGuests();
    this.groupStep = 2;
    this._fadeSwap(this.elStep1, this.elStep2);

    // Type tabs disappear on the bundles step.
    fadeOut(this.elTypeRow, function () { if (self.elTypeRow) self.elTypeRow.hidden = true; });
    // Swap the CTA buttons with a fade.
    fadeOut(this.elCtaRow, function () { self.refreshCta(); fadeIn(self.elCtaRow); });
    // Re-evaluate the scroll fade once step 2 is actually visible/measured.
    setTimeout(function () { self.updateGuestsScroll(); }, 600);
  };

  Booking.prototype.groupBack = function () {
    var self = this;
    this.groupStep = 1;
    this._fadeSwap(this.elStep2, this.elStep1);

    // Bring the type tabs back, fading in.
    if (this.elTypeRow) {
      this.elTypeRow.hidden = false;
      requestAnimationFrame(function () { fadeIn(self.elTypeRow); });
    }
    fadeOut(this.elCtaRow, function () { self.refreshCta(); fadeIn(self.elCtaRow); });
  };

  Booking.prototype.buildGuests = function () {
    var self = this;
    if (!this.elGuests) return;
    this.elGuests.innerHTML = '';
    this.guestPickers = [];

    var variants = (this.type && this.type.variants) || [];
    var ctx = {
      moneyFormat: this.config.moneyFormat,
      icon: function (n) { return self.icon(n); },
      addLabel: this.config.addSecondLabel || 'Add a second workshop',
      removeLabel: this.config.removeSecondLabel || 'Remove second workshop'
    };
    var labelPrefix = this.config.guestLabel || 'Guest';
    var onChange = function () {
      self.refreshCta();
      self.updateGuestsScroll();
      setTimeout(function () { self.updateGuestsScroll(); }, 350); // after add/remove wipe
    };

    for (var i = 0; i < this.participants; i++) {
      var block = elem('div', 'wb-guest flex flex-col gap-1.5');
      block.appendChild(elem('span', 'p opacity-70', labelPrefix + ' ' + (i + 1)));
      var picker = createGuestPicker(ctx, variants, onChange);
      block.appendChild(picker.el);
      this.elGuests.appendChild(block);
      this.guestPickers.push(picker);
    }

    if (!this.elGuests.__wbScrollBound) {
      this.elGuests.__wbScrollBound = true;
      this.elGuests.addEventListener('scroll', function () { self.updateGuestsScroll(); });
    }
    requestAnimationFrame(function () { self.updateGuestsScroll(); });
  };

  // Make the whole card scrollable only while its content overflows the
  // max-height clamp. scrollHeight reflects the full content even when the box
  // is clamped with overflow visible, so this stays accurate as steps/guests
  // grow. The small threshold ignores sub-pixel overflow that would otherwise
  // leave the card permanently (and needlessly) scrollable.
  Booking.prototype.updateCardScroll = function () {
    var el = this.root;
    if (!el) return;
    var overflowing = el.scrollHeight > el.clientHeight + 4;
    var want = overflowing ? 'auto' : '';
    if (el.style.overflowY !== want) {
      el.style.overflowY = want;
      el.style.overscrollBehavior = overflowing ? 'contain' : '';
    }
  };

  // Recompute the card's scrollability whenever its content or the viewport
  // changes size. Observes the inner content wrappers too, since the card box
  // stops growing once clamped at max-height.
  Booking.prototype._watchCardScroll = function () {
    var self = this;
    var recompute = function () { self.updateCardScroll(); };
    if (window.ResizeObserver) {
      this._cardRO = new ResizeObserver(recompute);
      [this.root, this.elBody, this.elParty].forEach(function (el) {
        if (el) self._cardRO.observe(el);
      });
    }
    window.addEventListener('resize', recompute);
    requestAnimationFrame(recompute);
  };

  // Show the bottom fade only while the guests list is scrollable and not yet
  // scrolled to the end.
  Booking.prototype.updateGuestsScroll = function () {
    var el = this.elGuests;
    if (!el) return;
    var overflowing = el.scrollHeight > el.clientHeight + 1;
    var atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
    // Animate the fade in/out via the registered --wb-fade length.
    el.style.setProperty('--wb-fade', (overflowing && !atBottom) ? '2.5rem' : '0px');
  };

  Booking.prototype._groupTotal = function () {
    var self = this;
    var total = 0;
    this.guestPickers.forEach(function (p) { total += self._sumPrices(p.getValues()); });
    return total;
  };

  // Group: one seat / one line item per chosen workshop, all on the same slot.
  Booking.prototype.bookGroup = function () {
    if (!this.selectedSlot) return;
    var props = this._properties();
    var items = [];
    this.guestPickers.forEach(function (p) {
      p.getValues().forEach(function (id) {
        items.push({ id: Number(id), quantity: 1, properties: props });
      });
    });
    if (items.length) this._checkout(items);
  };

  /* ------------------------------ party ----------------------------- */

  // Build the party form's JS widgets: a wipe-open date picker and a guests
  // stepper, each feeding a hidden input the native contact form submits.
  Booking.prototype.setupParty = function () {
    var self = this;
    var icon = function (n) { return self.icon(n); };

    // Date picker — field sits in its column, the calendar opens full-width below.
    var dateBox = this.root.querySelector('[data-wb-party-date]');
    var calBox = this.root.querySelector('[data-wb-party-calendar]');
    var dateInput = this.root.querySelector('[data-wb-party-date-input]');
    if (dateBox && !dateBox.__wbInit) {
      dateBox.__wbInit = true;
      var picker = createDatePicker(
        { icon: icon, monthsAhead: 12, placeholder: this.config.partyDatePlaceholder || 'Select a date' },
        function (iso, label) { if (dateInput) dateInput.value = label; }
      );
      dateBox.appendChild(picker.field);
      (calBox || dateBox).appendChild(picker.calendar);
    }

    // Time — same custom dropdown as the workshop bundle, with half-hour values.
    var timeBox = this.root.querySelector('[data-wb-party-time]');
    var timeInput = this.root.querySelector('[data-wb-party-time-input]');
    if (timeBox && !timeBox.__wbInit) {
      timeBox.__wbInit = true;
      var times = halfHourTimes(this.config.partyStartHour, this.config.partyEndHour);
      var timeDd = createDropdown({ icon: icon }, textItems(times), times[0], function (val) {
        if (timeInput) timeInput.value = val;
      });
      if (timeInput) timeInput.value = timeDd.getValue();
      timeBox.appendChild(timeDd.el);
    }

    var stepBox = this.root.querySelector('[data-wb-party-stepper]');
    var guestsInput = this.root.querySelector('[data-wb-party-guests-input]');
    if (stepBox && !stepBox.__wbInit) {
      stepBox.__wbInit = true;
      var stepper = createStepper(
        { icon: icon, min: this.config.partyMinGuests || 1, max: this.config.partyMaxGuests || 16, value: 2, fieldClass: 'bg-background' },
        function (val) { if (guestsInput) guestsInput.value = String(val); }
      );
      stepBox.appendChild(stepper.el);
    }
  };

  /* --------------------------- active type -------------------------- */

  // The type tabs are now links to each type's own page, so the active type is
  // fixed for this page — we just show the matching body once.
  Booking.prototype.applyActiveType = function () {
    var isParty = this.typeKey === 'party';
    var isSolo = this.typeKey === 'solo';
    var isGroup = this.typeKey === 'group';
    if (this.elBody) this.elBody.hidden = isParty;
    if (this.elParty) this.elParty.hidden = !isParty;
    if (this.elCtaRow) this.elCtaRow.hidden = isParty;
    // Solo shows the single bundle dropdown; Group shows the participants
    // stepper. Step 2 stays hidden (not just opacity-0, so it takes no space)
    // until "Continue to bundles" — _fadeSwap toggles it.
    if (this.elBundleSolo) this.elBundleSolo.hidden = !isSolo;
    if (this.elParticipants) this.elParticipants.hidden = !isGroup;
    if (this.elStep2) this.elStep2.hidden = true;
  };

  /* ------------------------------ errors ---------------------------- */

  Booking.prototype.showError = function (msg) {
    if (!this.elError) return;
    this.elError.textContent = msg;
    this.elError.hidden = false;
  };
  Booking.prototype.clearError = function () {
    if (!this.elError) return;
    this.elError.textContent = '';
    this.elError.hidden = true;
  };

  /* --------------------------- boot --------------------------- */

  function init() {
    var root = document.querySelector('[data-workshop-booking]');
    if (!root || root.__wbInit) return;
    root.__wbInit = true;
    try { new Booking(root); }
    catch (e) { console.error('[workshop-booking]', e); }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
